import React, { useState, useEffect } from "react";
import { Copy, ChevronDown, Save, Layout, Maximize, Sparkles, MessageSquare, Code, Type, Square, Plus, Brain, Zap } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";
import PageHeader from "../components/PageHeader";
import Dropdown from "../components/Dropdown";
import {
  visualIdentityIcon,
  advancedEngagementIcon,
  neuralContextIcon,
  deploymentScriptIcon,
  identityAvatarIcon,
  liveEngagementIcon
} from "../components/icons";

const hexToRgba = (hex, opacityPercent = 100) => {
  if (!hex) return "#4c3850";
  const alpha = Math.max(0, Math.min(100, Number(opacityPercent))) / 100;
  let c = hex.replace("#", "").trim();
  if (c.startsWith("rgb") || c.startsWith("hsl")) {
    const parts = c.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      if (alpha === 1) {
        return `#${parseInt(parts[0], 10).toString(16).padStart(2, "0")}${parseInt(parts[1], 10).toString(16).padStart(2, "0")}${parseInt(parts[2], 10).toString(16).padStart(2, "0")}`;
      }
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
    }
  }
  if (c.length === 3) c = c.split("").map((x) => x + x).join("");
  if (c.length === 8) c = c.slice(0, 6);
  const num = parseInt(c, 16);
  if (isNaN(num)) return hex;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  if (alpha === 1) {
    return `#${c.toUpperCase()}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const extractAlpha = (colorStr) => {
  if (!colorStr) return 100;
  if (colorStr.startsWith("rgba")) {
    const match = colorStr.match(/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/);
    if (match) return Math.round(parseFloat(match[1]) * 100);
  }
  if (colorStr.startsWith("#") && colorStr.length === 9) {
    const a = parseInt(colorStr.slice(7, 9), 16);
    return Math.round((a / 255) * 100);
  }
  return 100;
};

const extractBaseHex = (colorStr, fallback = "#4c3850") => {
  if (!colorStr || typeof colorStr !== "string") return fallback;
  if (colorStr.includes("gradient")) {
    const token = colorStr.match(/#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)/)?.[0];
    if (token) {
      return extractBaseHex(token, fallback);
    }
  }
  if (colorStr.startsWith("rgba") || colorStr.startsWith("rgb")) {
    const parts = colorStr.match(/\d+/g);
    if (parts && parts.length >= 3) {
      const r = parseInt(parts[0], 10).toString(16).padStart(2, "0");
      const g = parseInt(parts[1], 10).toString(16).padStart(2, "0");
      const b = parseInt(parts[2], 10).toString(16).padStart(2, "0");
      return `#${r}${g}${b}`;
    }
  }
  if (colorStr.startsWith("#")) {
    if (colorStr.length === 9) return colorStr.slice(0, 7);
    if (colorStr.length === 7) return colorStr;
    if (colorStr.length === 4) {
      return "#" + colorStr.slice(1).split("").map((x) => x + x).join("");
    }
  }
  return fallback;
};

const parseGradient = (val, fallbackStart = "#9ae0bf", fallbackEnd = "#c38dce") => {
  if (typeof val === "string" && val.includes("gradient")) {
    const angleMatch = val.match(/(\d+)deg/);
    const angle = angleMatch ? angleMatch[1] : "135";
    const colors = val.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/g);
    const rawStart = colors?.[0] || fallbackStart;
    const rawEnd = colors?.[1] || fallbackEnd;
    return {
      isGradient: true,
      angle: angle,
      startHex: extractBaseHex(rawStart, fallbackStart),
      startOpacity: extractAlpha(rawStart),
      endHex: extractBaseHex(rawEnd, fallbackEnd),
      endOpacity: extractAlpha(rawEnd),
    };
  }
  const raw = (typeof val === "string" && (val.startsWith("#") || val.startsWith("rgb"))) ? val : fallbackStart;
  return {
    isGradient: false,
    angle: "135",
    startHex: extractBaseHex(raw, fallbackStart),
    startOpacity: extractAlpha(raw),
    endHex: extractBaseHex(fallbackEnd, fallbackEnd),
    endOpacity: 100,
  };
};

const WidgetIntegration = ({ activeAiAgentId, aiAgents, fetchAiAgents }) => {
  const { showToast } = useToast();
  const [widgetConfig, setWidgetConfig] = useState({
    position: "",
    welcomeMessage: "",
    title: "",
    iconUrl: "",
    brandLogoUrl: "",
    headerColor: "#4c3850",
    headerTitleColor: "#ffffff",
    userBubbleColor: "#4c3850",
    botBubbleColor: "#ffffff",
    sendButtonColor: "#4c3850",
    size: "medium",
    launcherSize: "60px",
    showCloseButton: true,
    showLauncher: true,
    eyecatcherEnabled: true,
    eyecatcherText: "👋 Ask me anything!",
    eyecatcherTheme: "gradient",
    eyecatcherAnimation: "pulse",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    borderRadius: "20px",
    customParameters: [],
    campaignSequentialDisplay: true,
    learningEnabled: true,
    feedbackEnabled: true,
    internetSearchEnabled: false,
    summaryEnabled: true,
    summaryInterval: 5,
    summaryModelId: "gpt-4o-mini",
    mode: "multi",
    streamingEnabled: true,
    responseMode: "ai",
    humanEscalation: true,
  });
  const currentAiAgent = aiAgents?.find((p) => p.id === activeAiAgentId);
  const currentAiAgentKey = currentAiAgent?.ai_agent_key || "";
  const [uploading, setUploading] = useState(false);
  const [iconPreview, setIconPreview] = useState(null);
  const [brandLogoPreview, setBrandLogoPreview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeColorTarget, setActiveColorTarget] = useState("headerColor");
  const [hoveredPreviewElement, setHoveredPreviewElement] = useState(null);

  const scrollToColorCustomization = (targetKey) => {
    setActiveColorTarget(targetKey);
    const el = document.getElementById("color-customization-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-indigo-400", "ring-offset-2");
      setTimeout(() => {
        el.classList.remove("ring-4", "ring-indigo-400", "ring-offset-2");
      }, 1500);
    }
  };

  const SUMMARY_MODEL_OPTIONS = [
    { value: "gpt-4o-mini", label: "GPT-4o Mini (Cost Optimized)" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo (Standard)" },
    { value: "gpt-4o", label: "GPT-4o (High Precision)" },
    { value: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku" },
    { value: "gemini-flash-latest", label: "Gemini 1.5 Flash (Latest)" },
  ];

  const RESERVED_KEYS = [
    "apiUrl", "aiAgentId", "agentKey", "showCloseButton", "showLauncher",
    "title", "welcomeMessage", "position", "size", "primaryColor",
    "secondaryColor", "backgroundColor", "textColor", "borderRadius",
    "width", "height", "fontSize", "fontFamily", "isPreview"
  ];

  const positionOptions = [
    { value: "", label: "Select Position" },
    { value: "bottom-right", label: "Bottom Right" },
    { value: "bottom-left", label: "Bottom Left" },
    { value: "top-right", label: "Top Right" },
    { value: "top-left", label: "Top Left" },
  ];

  const sizeOptions = [
    { value: "small", label: "Small (300×400)" },
    { value: "medium", label: "Medium (350×500)" },
    { value: "large", label: "Large (400×600)" },
  ];



  const fontOptions = [
    { label: 'Inter (Default)', value: 'Inter, sans-serif', style: { fontFamily: 'Inter, sans-serif' } },
    { label: 'Roboto', value: 'Roboto, sans-serif', style: { fontFamily: 'Roboto, sans-serif' } },
    { label: 'Open Sans', value: '"Open Sans", sans-serif', style: { fontFamily: '"Open Sans", sans-serif' } },
    { label: 'Montserrat', value: 'Montserrat, sans-serif', style: { fontFamily: 'Montserrat, sans-serif' } },
    { label: 'Poppins', value: 'Poppins, sans-serif', style: { fontFamily: 'Poppins, sans-serif' } },
    { label: 'Playfair Display', value: '"Playfair Display", serif', style: { fontFamily: '"Playfair Display", serif' } },
    { label: 'Lobster', value: 'Lobster, cursive', style: { fontFamily: 'Lobster, cursive' } },
    { label: 'Courier New', value: '"Courier New", monospace', style: { fontFamily: '"Courier New", monospace' } },
    { label: 'Georgia', value: 'Georgia, serif', style: { fontFamily: 'Georgia, serif' } },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", cursive', style: { fontFamily: '"Comic Sans MS", cursive' } },
    { label: 'System Sans', value: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', style: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' } }
  ];
  const eyecatcherThemeOptions = [
    { value: 'gradient', label: 'Gradient' },
    { value: 'glassmorphic', label: 'Glassmorphic' },
    { value: 'classic', label: 'Classic' },
  ];
  const eyecatcherAnimationOptions = [
    { value: 'pulse', label: 'Pulse' },
    { value: 'shake', label: 'Shake' },
    { value: 'float', label: 'Float' },
  ];
  const [embedCode, setEmbedCode] = useState("");
  const COMMON_PARAMS = [
    { label: "User Full Name", value: "name" },
    { label: "Email Address", value: "email" },
    { label: "Phone Number", value: "phone" },
    { label: "Custom User ID", value: "userid" },
  ];

  const backendUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
  const frontendUrl = backendUrl;

  useEffect(() => {
    if (!aiAgents || aiAgents.length === 0) {
      fetchAiAgents();
    }
  }, [aiAgents, fetchAiAgents]);

  useEffect(() => {
    const loadAllConfig = async () => {
      try {
        if (activeAiAgentId) {
          api.setAiAgentId(activeAiAgentId);
        }
        const [configResponse, campaignsList, logoData] = await Promise.all([
          api.getWidgetConfig(),
          api.getCampaigns().catch(() => []),
          api.getCurrentLogo().catch(() => null)
        ]);

        if (configResponse) {
          setWidgetConfig({
            position: configResponse.position || "",
            welcomeMessage: configResponse.welcome_message || "",
            title: configResponse.title || "",
            iconUrl: configResponse.icon_url || "",
            brandLogoUrl: configResponse.brand_logo_url || "",
            headerColor: configResponse.header_color || "#4c3850",
            headerTitleColor: configResponse.header_title_color || "#ffffff",
            userBubbleColor: configResponse.user_bubble_color || "#4c3850",
            botBubbleColor: configResponse.bot_bubble_color || "#ffffff",
            sendButtonColor: configResponse.send_button_color || "#4c3850",
            size: configResponse.size || "medium",
            launcherSize: configResponse.launcher_size || "60px",
            showCloseButton: configResponse.show_close_button !== undefined ? configResponse.show_close_button : true,
            showLauncher: configResponse.show_launcher !== undefined ? configResponse.show_launcher : true,
            eyecatcherEnabled: configResponse.eyecatcher_enabled !== undefined ? configResponse.eyecatcher_enabled : true,
            eyecatcherText: configResponse.eyecatcher_text || "👋 Ask me anything!",
            eyecatcherTheme: configResponse.eyecatcher_theme || "gradient",
            eyecatcherAnimation: configResponse.eyecatcher_animation || "pulse",
            fontFamily: configResponse.font_family || "Inter, sans-serif",
            fontSize: configResponse.font_size || "14px",
            borderRadius: configResponse.border_radius || "20px",
            campaignSequentialDisplay: configResponse.campaign_sequential_display !== undefined ? configResponse.campaign_sequential_display : true,
            learningEnabled: configResponse.learning_enabled !== undefined ? configResponse.learning_enabled : true,
            feedbackEnabled: configResponse.feedback_enabled !== undefined ? configResponse.feedback_enabled : true,
            internetSearchEnabled: configResponse.internet_search_enabled !== undefined ? configResponse.internet_search_enabled : false,
            summaryEnabled: configResponse.summary_enabled !== undefined ? configResponse.summary_enabled : true,
            summaryInterval: configResponse.summary_interval || 5,
            summaryModelId: configResponse.summary_model_id || "gpt-4o-mini",
            customParameters: configResponse.custom_parameters || [],
            mode: configResponse.mode || "multi",
            streamingEnabled: configResponse.streaming_enabled !== undefined ? configResponse.streaming_enabled : true,
            responseMode: configResponse.response_mode || "ai",
            humanEscalation: configResponse.human_escalation !== undefined ? configResponse.human_escalation : true,
          });

          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          if (configResponse.icon_url) {
            setIconPreview(`${baseUrl}${configResponse.icon_url}`);
          }
        }

        if (logoData && logoData.logo_url) {
          setBrandLogoPreview(logoData.logo_url);
        } else if (configResponse?.brand_logo_url) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          const logoUrl = configResponse.brand_logo_url.startsWith("http")
            ? configResponse.brand_logo_url
            : `${baseUrl}${configResponse.brand_logo_url.startsWith("/") ? "" : "/"}${configResponse.brand_logo_url}`;
          setBrandLogoPreview(logoUrl);
        } else if (activeAiAgentId) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          setBrandLogoPreview(`${baseUrl}/api/widget/assets/brand-logo?ai_agent_id=${activeAiAgentId}`);
        }

        if (campaignsList) setCampaigns(campaignsList);
      } catch (error) {
        console.error("Error loading widget data:", error);
      }
    };

    if (activeAiAgentId) {
      loadAllConfig();
    }
  }, [activeAiAgentId]);


  useEffect(() => {
    // Sync current (possibly unsaved) config to sessionStorage for the preview tab
    sessionStorage.setItem('widget_preview_config', JSON.stringify(widgetConfig));
  }, [widgetConfig]);

  // Outside clicks are handled locally by the Dropdown component

  useEffect(() => {
    const generateEmbedCode = () => {
      const userStr = sessionStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      const tenantId = currentAiAgent?.tenant_id || user?.tenant_id || "";

      const code = `<script src="${frontendUrl}/widget.js"></script>
<script>
  ChatWidget.init({
    apiUrl: '${backendUrl}',
    aiAgentId: '${activeAiAgentId || ""}',
    tenantId: '${tenantId}',
    agentKey: '${currentAiAgentKey}',
    showCloseButton: ${widgetConfig.showCloseButton},
    showLauncher: ${widgetConfig.showLauncher}${widgetConfig.customParameters.length > 0 ? ',\n    // Custom Parameters:\n    ' + widgetConfig.customParameters.map(p => `${p}: 'VALUE'`).join(',\n    ') : ''}
  });
</script>`;
      setEmbedCode(code);
    };

    if (widgetConfig.title) {
      generateEmbedCode();
    }
  }, [widgetConfig.title, currentAiAgentKey, activeAiAgentId, currentAiAgent, frontendUrl, backendUrl, widgetConfig.showCloseButton, widgetConfig.showLauncher, widgetConfig.customParameters]);

  const saveConfig = async (overrideConfig = null, successMessage = "Configuration saved successfully!") => {
    // Validate launcher size (max 150px)
    if (!overrideConfig && widgetConfig.launcherSize) {
      const parsedSize = parseInt(widgetConfig.launcherSize, 10);
      if (!isNaN(parsedSize) && parsedSize > 150) {
        showToast("Launcher size cannot exceed 150px", "error");
        return;
      }
    } else if (overrideConfig && overrideConfig.launcher_size) {
      const parsedSize = parseInt(overrideConfig.launcher_size, 10);
      if (!isNaN(parsedSize) && parsedSize > 150) {
        showToast("Launcher size cannot exceed 150px", "error");
        return;
      }
    }
    try {
      if (overrideConfig) {
        // Use PATCH for specific field updates
        await api.patchWidgetConfig(overrideConfig);
      } else {
        // Use PATCH for the main form save to avoid overwriting assets
        const partialConfig = {
          position: widgetConfig.position,
          welcome_message: widgetConfig.welcomeMessage,
          title: widgetConfig.title,
          header_color: widgetConfig.headerColor,
          header_title_color: widgetConfig.headerTitleColor,
          user_bubble_color: widgetConfig.userBubbleColor,
          bot_bubble_color: widgetConfig.botBubbleColor,
          send_button_color: widgetConfig.sendButtonColor,
          size: widgetConfig.size,
          launcher_size: widgetConfig.launcherSize,
          font_family: widgetConfig.fontFamily,
          font_size: widgetConfig.fontSize,
          border_radius: widgetConfig.borderRadius,
          campaign_sequential_display: widgetConfig.campaignSequentialDisplay,
          learning_enabled: widgetConfig.learningEnabled,
          feedback_enabled: widgetConfig.feedbackEnabled,
          summary_enabled: widgetConfig.summaryEnabled,
          summary_interval: widgetConfig.summaryInterval,
          summary_model_id: widgetConfig.summaryModelId,
          eyecatcher_enabled: widgetConfig.eyecatcherEnabled,
          eyecatcher_text: widgetConfig.eyecatcherText,
          eyecatcher_theme: widgetConfig.eyecatcherTheme,
          eyecatcher_animation: widgetConfig.eyecatcherAnimation,
          mode: widgetConfig.mode,
          streaming_enabled: widgetConfig.streamingEnabled,
          response_mode: widgetConfig.responseMode,
          human_escalation: widgetConfig.humanEscalation,
          internet_search_enabled: widgetConfig.internetSearchEnabled,
        };
        await api.patchWidgetConfig(partialConfig);
      }
      showToast(successMessage, "success");
    } catch (error) {
      console.error("Error saving config:", error);
      showToast("Failed to save configuration", "error");
    }
  };

  const handleIconUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      await api.uploadWidgetIcon(file);

      const updatedConfig = await api.getWidgetConfig();
      if (updatedConfig) {
        setWidgetConfig(prev => ({
          ...prev,
          iconUrl: updatedConfig.icon_url,
          brandLogoUrl: updatedConfig.brand_logo_url,
        }));

        if (updatedConfig.icon_url) {
          const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace("/api", "");
          setIconPreview(`${baseUrl}${updatedConfig.icon_url}`);
        }
      }

      showToast("Icon uploaded successfully!", "success");
    } catch (error) {
      console.error("Error uploading icon:", error);
      showToast("Failed to upload icon", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleIconUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      showToast("Code copied to clipboard!", "success");
    } catch (error) {
      console.error("Error copying embed code:", error);
      showToast("Failed to copy code", "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-6 lg:pt-6 lg:pb-12 lg:px-10 animate-fadeIn">
      <div className="w-full max-w-[1560px] mx-auto">
        <div className="mb-8">
          <PageHeader
            title="Widget Interface"
            description="Engineered for seamless integration. Orchestrate your brand's digital presence with a bespoke neural touchpoint."
          />
        </div>

        {/* Two-column layout: Settings (left) + Preview (right) */}
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', width: '100%', flexWrap: 'nowrap' }}>

          {/* LEFT: Settings Panel */}
          <div style={{ flex: 1, minWidth: '460px', display: 'flex', flexDirection: 'column', gap: '32px' }} className="animate-slideIn">
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={visualIdentityIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Visual Identity</h3>
                  <p className="text-[11px] font-medium text-gray-400">Configure layout and color palette</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8 mt-10">
                {/* Row 1 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Layout size={12} /> Position
                  </label>
                  <Dropdown
                    options={positionOptions}
                    value={widgetConfig.position}
                    onChange={(val) => setWidgetConfig({ ...widgetConfig, position: val })}
                    buttonClassName="bg-gray-50 border-0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Maximize size={12} /> Initial Size
                  </label>
                  <Dropdown
                    options={sizeOptions}
                    value={widgetConfig.size}
                    onChange={(val) => setWidgetConfig({ ...widgetConfig, size: val })}
                    buttonClassName="bg-gray-50 border-0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Maximize size={12} /> Launcher Size
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.launcherSize}
                    onChange={(e) =>
                      setWidgetConfig({ ...widgetConfig, launcherSize: e.target.value })
                    }
                    placeholder="E.g., 60px or 70"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <MessageSquare size={12} /> Widget Display Title
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.title}
                    onChange={(e) =>
                      setWidgetConfig({ ...widgetConfig, title: e.target.value })
                    }
                    placeholder="E.g., Customer Support"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Sparkles size={12} /> Passive Welcome Hook
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.welcomeMessage}
                    onChange={(e) =>
                      setWidgetConfig({
                        ...widgetConfig,
                        welcomeMessage: e.target.value,
                      })
                    }
                    placeholder="How can we help you today?"
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                  />
                </div>

                {/* Row 3 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Type size={12} /> Font Family
                  </label>
                  <Dropdown
                    options={fontOptions}
                    value={widgetConfig.fontFamily}
                    onChange={(val) => setWidgetConfig({ ...widgetConfig, fontFamily: val })}
                    buttonClassName="bg-gray-50 border-0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Type size={12} /> Font Size
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.fontSize}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, fontSize: e.target.value })}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                    placeholder="14px"
                  />
                </div>

                {/* Row 4 */}
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                    <Square size={12} /> Border Radius
                  </label>
                  <input
                    type="text"
                    value={widgetConfig.borderRadius}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, borderRadius: e.target.value })}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                    placeholder="20px"
                  />
                </div>
              </div>

              {/* Color Customization Area */}
              {(() => {
                const COLOR_TABS = [
                  { key: "headerColor", label: "Header", defaultStart: "#9ae0bf", defaultEnd: "#c38dce" },
                  { key: "headerTitleColor", label: "Header Title", defaultStart: "#ffffff", defaultEnd: "#f1f5f9" },
                  { key: "userBubbleColor", label: "User Bubble", defaultStart: "#c38dce", defaultEnd: "#8b5cf6" },
                  { key: "botBubbleColor", label: "Bot Bubble", defaultStart: "#ffffff", defaultEnd: "#f1f5f9" },
                  { key: "sendButtonColor", label: "Action Button", defaultStart: "#55b99b", defaultEnd: "#9ae0bf" },
                ];

                const currentTab = COLOR_TABS.find((t) => t.key === activeColorTarget) || COLOR_TABS[0];
                const currentVal = widgetConfig[currentTab.key] || currentTab.defaultStart;
                const isLinear = typeof currentVal === "string" && currentVal.includes("gradient");
                const grad = parseGradient(currentVal, currentTab.defaultStart, currentTab.defaultEnd);

                const handleModeSwitch = (toLinear) => {
                  if (toLinear) {
                    const s = hexToRgba(grad.startHex, grad.startOpacity);
                    const e = hexToRgba(grad.endHex, grad.endOpacity);
                    setWidgetConfig({
                      ...widgetConfig,
                      [currentTab.key]: `linear-gradient(${grad.angle}deg, ${s} 0%, ${e} 100%)`,
                    });
                  } else {
                    setWidgetConfig({
                      ...widgetConfig,
                      [currentTab.key]: hexToRgba(grad.startHex, grad.startOpacity),
                    });
                  }
                };

                const updateGradient = (newStartHex, newEndHex, newAngle, newStartOp, newEndOp) => {
                  const sHex = newStartHex !== undefined ? newStartHex : grad.startHex;
                  const eHex = newEndHex !== undefined ? newEndHex : grad.endHex;
                  const angle = newAngle !== undefined ? newAngle : grad.angle;
                  const sOp = newStartOp !== undefined ? newStartOp : grad.startOpacity;
                  const eOp = newEndOp !== undefined ? newEndOp : grad.endOpacity;

                  const s = hexToRgba(sHex, sOp);
                  const e = hexToRgba(eHex, eOp);

                  setWidgetConfig({
                    ...widgetConfig,
                    [currentTab.key]: `linear-gradient(${angle}deg, ${s} 0%, ${e} 100%)`,
                  });
                };

                const updateSolid = (newHex, newOp) => {
                  const hex = newHex !== undefined ? newHex : grad.startHex;
                  const op = newOp !== undefined ? newOp : grad.startOpacity;
                  setWidgetConfig({
                    ...widgetConfig,
                    [currentTab.key]: hexToRgba(hex, op),
                  });
                };

                return (
                  <div id="color-customization-section" className="mt-8 p-5 bg-gray-50 rounded-2xl border border-gray-100 space-y-5 transition-all duration-300 scroll-mt-8">
                    {/* Header: Element Tabs on Left, Solid/Linear Switch on Right */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-200">
                      {/* Left: Element Selector Tabs */}
                      <div className="flex flex-wrap items-center gap-2">
                        {COLOR_TABS.map((tab) => {
                          const isActive = activeColorTarget === tab.key;
                          const tabVal = widgetConfig[tab.key] || tab.defaultStart;
                          return (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveColorTarget(tab.key)}
                              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                                isActive
                                  ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-200/60"
                              }`}
                            >
                              <span
                                className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0 shadow-2xs"
                                style={{ background: tabVal }}
                              />
                              {tab.label}
                            </button>
                          );
                        })}
                      </div>

                      {/* Right: Solid vs Linear Toggle */}
                      <div className="flex items-center bg-gray-200/80 p-1 rounded-xl gap-1 shrink-0 self-start sm:self-auto">
                        <button
                          type="button"
                          onClick={() => handleModeSwitch(false)}
                          className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                            !isLinear
                              ? "bg-white text-gray-900 shadow-sm"
                              : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          Solid
                        </button>
                        <button
                          type="button"
                          onClick={() => handleModeSwitch(true)}
                          className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                            isLinear
                              ? "bg-white text-gray-900 shadow-sm"
                              : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          Linear
                        </button>
                      </div>
                    </div>

                    {/* Color Pickers + Opacity + Angle Controls */}
                    <div className="space-y-4">
                      {isLinear ? (
                        <>
                          {/* Angle Selector Bar */}
                          <div className="p-3 bg-white rounded-xl border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0">
                                Angle ({grad.angle}°)
                              </span>
                              <input
                                type="range"
                                min="0"
                                max="360"
                                value={grad.angle}
                                onChange={(e) => updateGradient(undefined, undefined, e.target.value, undefined, undefined)}
                                className="w-full max-w-[200px] h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                              />
                            </div>
                            <div className="flex items-center gap-1.5">
                              {["90", "135", "180", "45"].map((deg) => (
                                <button
                                  key={deg}
                                  type="button"
                                  onClick={() => updateGradient(undefined, undefined, deg, undefined, undefined)}
                                  className={`px-2 py-0.5 text-[10px] font-bold rounded-md border transition-all ${
                                    grad.angle === deg
                                      ? "bg-gray-900 text-white border-gray-900"
                                      : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                                  }`}
                                >
                                  {deg}°
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Start & End Color Pickers with Opacity */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Start Color Column */}
                            <div className="p-3 bg-white rounded-xl border border-gray-200 space-y-3">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Start Color</label>
                                <span className="text-[10px] font-mono font-bold text-gray-500 uppercase">{grad.startHex}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={grad.startHex}
                                  onChange={(e) => updateGradient(e.target.value, undefined, undefined, undefined, undefined)}
                                  className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent overflow-hidden shrink-0"
                                />
                                <input
                                  type="text"
                                  value={grad.startHex}
                                  onChange={(e) => updateGradient(e.target.value, undefined, undefined, undefined, undefined)}
                                  className="text-xs font-mono font-bold uppercase text-gray-700 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 outline-none w-full"
                                />
                              </div>
                              <div className="space-y-1 pt-1 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Opacity</span>
                                  <span className="text-[10px] font-mono font-bold text-gray-600">{grad.startOpacity}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={grad.startOpacity}
                                  onChange={(e) => updateGradient(undefined, undefined, undefined, e.target.value, undefined)}
                                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                                />
                              </div>
                            </div>

                            {/* End Color Column */}
                            <div className="p-3 bg-white rounded-xl border border-gray-200 space-y-3">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">End Color</label>
                                <span className="text-[10px] font-mono font-bold text-gray-500 uppercase">{grad.endHex}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={grad.endHex}
                                  onChange={(e) => updateGradient(undefined, e.target.value, undefined, undefined, undefined)}
                                  className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent overflow-hidden shrink-0"
                                />
                                <input
                                  type="text"
                                  value={grad.endHex}
                                  onChange={(e) => updateGradient(undefined, e.target.value, undefined, undefined, undefined)}
                                  className="text-xs font-mono font-bold uppercase text-gray-700 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 outline-none w-full"
                                />
                              </div>
                              <div className="space-y-1 pt-1 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Opacity</span>
                                  <span className="text-[10px] font-mono font-bold text-gray-600">{grad.endOpacity}%</span>
                                </div>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={grad.endOpacity}
                                  onChange={(e) => updateGradient(undefined, undefined, undefined, undefined, e.target.value)}
                                  className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="p-3 bg-white rounded-xl border border-gray-200 space-y-3 max-w-sm">
                          <div className="flex items-center justify-between">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Color</label>
                            <span className="text-[10px] font-mono font-bold text-gray-500 uppercase">{grad.startHex}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={grad.startHex}
                              onChange={(e) => updateSolid(e.target.value, undefined)}
                              className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent overflow-hidden shrink-0"
                            />
                            <input
                              type="text"
                              value={grad.startHex}
                              onChange={(e) => updateSolid(e.target.value, undefined)}
                              className="text-xs font-mono font-bold uppercase text-gray-700 bg-gray-50 px-2 py-1 rounded-md border border-gray-200 outline-none w-full"
                            />
                          </div>
                          <div className="space-y-1 pt-1 border-t border-gray-100">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Opacity</span>
                              <span className="text-[10px] font-mono font-bold text-gray-600">{grad.startOpacity}%</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={grad.startOpacity}
                              onChange={(e) => updateSolid(undefined, e.target.value)}
                              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}



              <div className="mt-10 p-6 bg-gray-50 rounded-3xl border border-gray-100 space-y-8">
                <div className="flex flex-col md:flex-row gap-8 justify-between">
                  <div className="flex items-center justify-between gap-10 flex-1">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">Display Header Close Button</h4>
                      <p className="text-[10px] text-gray-400 font-medium tracking-tight">Toggle the visibility of the "X" button in chat window</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={widgetConfig.showCloseButton}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setWidgetConfig({ ...widgetConfig, showCloseButton: newValue });
                          saveConfig({ show_close_button: newValue });
                        }}
                      />
                      <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
                    </label>
                  </div>

                  <div className="w-px bg-gray-200 hidden md:block"></div>

                  <div className="flex items-center justify-between gap-10 flex-1">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">Show Floating Launcher</h4>
                      <p className="text-[10px] text-gray-400 font-medium tracking-tight">Hide icon to auto-open chat directly on page load</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={widgetConfig.showLauncher}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setWidgetConfig({ ...widgetConfig, showLauncher: newValue });
                          try {
                            await api.patchWidgetConfig({ show_launcher: newValue });
                            showToast("Launcher visibility updated!", "success");
                          } catch (error) {
                            console.error("Error updating launcher:", error);
                            showToast("Failed to update launcher", "error");
                          }
                        }}
                      />
                      <div className="w-11 h-6 bg-white border-2 border-gray-200 rounded-full peer peer-checked:border-[#83bbe1] transition-all duration-300 after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all duration-300 peer-checked:after:translate-x-5 peer-checked:after:bg-[#83bbe1]"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-3xl border border-gray-100 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-gray-900">Eye Catcher Bubble</h4>
                    <p className="text-[11px] font-medium text-gray-400">Show a floating prompt to invite users into the chat experience.</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newValue = !widgetConfig.eyecatcherEnabled;
                      setWidgetConfig({ ...widgetConfig, eyecatcherEnabled: newValue });
                      try {
                        await api.patchWidgetConfig({ eyecatcher_enabled: newValue });
                        showToast(newValue ? "Eye Catcher enabled" : "Eye Catcher disabled", "success");
                      } catch (error) {
                        console.error("Error updating Eye Catcher setting:", error);
                        showToast("Failed to update Eye Catcher", "error");
                      }
                    }}
                    className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.eyecatcherEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                  >
                    <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.eyecatcherEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                  </button>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Eye Catcher Text</label>
                  <input
                    value={widgetConfig.eyecatcherText}
                    onChange={(e) => setWidgetConfig({ ...widgetConfig, eyecatcherText: e.target.value })}
                    onBlur={async (e) => {
                      try {
                        await api.patchWidgetConfig({ eyecatcher_text: e.target.value });
                        showToast("Eye Catcher text saved!", "success");
                      } catch (error) {
                        console.error("Error saving Eye Catcher text:", error);
                        showToast("Failed to save Eye Catcher text", "error");
                      }
                    }}
                    className="w-full px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 font-bold transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Theme</label>
                    <Dropdown
                      options={eyecatcherThemeOptions}
                      value={widgetConfig.eyecatcherTheme}
                      onChange={async (val) => {
                        setWidgetConfig({ ...widgetConfig, eyecatcherTheme: val });
                        try {
                          await api.patchWidgetConfig({ eyecatcher_theme: val });
                          showToast('Eye Catcher theme updated!', 'success');
                        } catch (error) {
                          console.error('Error updating Eye Catcher theme:', error);
                          showToast('Failed to update theme', 'error');
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Animation</label>
                    <Dropdown
                      options={eyecatcherAnimationOptions}
                      value={widgetConfig.eyecatcherAnimation}
                      onChange={async (val) => {
                        setWidgetConfig({ ...widgetConfig, eyecatcherAnimation: val });
                        try {
                          await api.patchWidgetConfig({ eyecatcher_animation: val });
                          showToast('Eye Catcher animation updated!', 'success');
                        } catch (error) {
                          console.error('Error updating Eye Catcher animation:', error);
                          showToast('Failed to update animation', 'error');
                        }
                      }}
                    />
                  </div>
                </div>

                <p className="text-[10px] text-gray-400">The Eye Catcher floats near the launcher and can be dismissed for the current session.</p>
              </div>
            </div>

            {/* Advanced Engagement Section */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50 mt-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={advancedEngagementIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Advanced Engagement</h3>
                  <p className="text-[11px] font-medium text-gray-400">Orchestrate how campaigns interact with users</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">Chatbot Response Type</h4>
                    <p className="text-[11px] text-gray-400 font-medium tracking-tight">Choose between dynamic AI Agent reasoning or structured Visual Botflows</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div
                      onClick={async () => {
                        setWidgetConfig({ ...widgetConfig, responseMode: "ai" });
                        try {
                          await api.patchWidgetConfig({ response_mode: "ai" });
                          showToast("AI Agent Mode Enabled", "success");
                        } catch (error) {
                          console.error("Error setting response mode:", error);
                          showToast("Failed to update response mode", "error");
                        }
                      }}
                      className={`p-4 rounded-xl cursor-pointer border transition-all ${widgetConfig.responseMode === "ai"
                          ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                          : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Brain size={16} className={widgetConfig.responseMode === "ai" ? "text-[#83bbe1]" : "text-gray-400"} />
                        <span className="text-xs font-bold text-gray-900">AI Agent</span>
                      </div>
                      <p className="text-[10px] text-gray-400 font-medium">Dynamic AI reasoning based on FAQs, instructions, and tools.</p>
                    </div>

                    <div
                      onClick={async () => {
                        setWidgetConfig({ ...widgetConfig, responseMode: "botflow" });
                        try {
                          await api.patchWidgetConfig({ response_mode: "botflow" });
                          showToast("Visual Botflow Mode Enabled", "success");
                        } catch (error) {
                          console.error("Error setting response mode:", error);
                          showToast("Failed to update response mode", "error");
                        }
                      }}
                      className={`p-4 rounded-xl cursor-pointer border transition-all ${widgetConfig.responseMode === "botflow"
                          ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                          : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Code size={16} className={widgetConfig.responseMode === "botflow" ? "text-blue-500" : "text-gray-400"} />
                        <span className="text-xs font-bold text-gray-900">Visual Botflow</span>
                      </div>
                      <p className="text-[10px] text-gray-400 font-medium">Structured client-side flowchart routes (quick replies, inputs, delay paths).</p>
                    </div>
                  </div>
                </div>

                {widgetConfig.responseMode === "ai" && (
                  <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1">AI Execution Mode</h4>
                      <p className="text-[11px] text-gray-400 font-medium tracking-tight">Choose how user queries are processed and executed</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div
                        onClick={async () => {
                          setWidgetConfig({ ...widgetConfig, mode: "single" });
                          try {
                            await api.patchWidgetConfig({ mode: "single" });
                            showToast("Solo Agent Mode Enabled", "success");
                          } catch (error) {
                            console.error("Error setting agent mode:", error);
                            showToast("Failed to update agent mode", "error");
                          }
                        }}
                        className={`p-4 rounded-xl cursor-pointer border transition-all ${widgetConfig.mode === "single"
                            ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                            : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                          }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Zap size={16} className={widgetConfig.mode === "single" ? "text-blue-500" : "text-gray-400"} />
                          <span className="text-xs font-bold text-gray-900">Solo Agent</span>
                        </div>
                        <p className="text-[10px] text-gray-400 font-medium">One AI agent handles the entire conversation for maximum speed.</p>
                      </div>

                      <div
                        onClick={async () => {
                          setWidgetConfig({ ...widgetConfig, mode: "multi" });
                          try {
                            await api.patchWidgetConfig({ mode: "multi" });
                            showToast("Agent Team Mode Enabled", "success");
                          } catch (error) {
                            console.error("Error setting agent mode:", error);
                            showToast("Failed to update agent mode", "error");
                          }
                        }}
                        className={`p-4 rounded-xl cursor-pointer border transition-all ${widgetConfig.mode === "multi"
                            ? "bg-white border-[#83bbe1] shadow-md shadow-blue-50"
                            : "bg-white border-gray-100 opacity-60 hover:opacity-100"
                          }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Brain size={16} className={widgetConfig.mode === "multi" ? "text-[#83bbe1]" : "text-gray-400"} />
                          <span className="text-xs font-bold text-gray-900">Agent Team</span>
                        </div>
                        <p className="text-[10px] text-gray-400 font-medium">A coordinated team of AI agents collaborates to provide deeper analysis and better decision-making.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-gray-900">Sequential Campaign Queue</h4>
                    <p className="text-[11px] font-medium text-gray-400">If multiple campaigns match, show them one after another instead of just the first one.</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newValue = !widgetConfig.campaignSequentialDisplay;
                      setWidgetConfig({ ...widgetConfig, campaignSequentialDisplay: newValue });
                      try {
                        await api.patchWidgetConfig({ campaign_sequential_display: newValue });
                        showToast("Sequential queue updated!", "success");
                      } catch (error) {
                        console.error("Error updating sequential queue:", error);
                        showToast("Failed to update queue setting", "error");
                      }
                    }}
                    className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.campaignSequentialDisplay ? "border-[#83bbe1]" : "border-gray-200"}`}
                  >
                    <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.campaignSequentialDisplay ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                  </button>
                </div>

                {widgetConfig.responseMode === "ai" && (
                  <>
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">AI Learning & Memory</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Enable background memory extraction.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.learningEnabled;
                          setWidgetConfig({ ...widgetConfig, learningEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ learning_enabled: newValue });
                            showToast(newValue ? "AI Learning Enabled" : "AI Learning Disabled", "success");
                          } catch (error) {
                            console.error("Error updating learning setting:", error);
                            showToast("Failed to update learning setting", "error");
                          }
                        }}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.learningEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.learningEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">Internet Search</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Allow the agent to search the web using DuckDuckGo.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.internetSearchEnabled;
                          setWidgetConfig({ ...widgetConfig, internetSearchEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ internet_search_enabled: newValue });
                            showToast(newValue ? "Internet Search Enabled" : "Internet Search Disabled", "success");
                          } catch (error) {
                            console.error("Error updating search setting:", error);
                            showToast("Failed to update search setting", "error");
                          }
                        }}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.internetSearchEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.internetSearchEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">Feedback System</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Allow users to rate and comment on neural responses.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.feedbackEnabled;
                          setWidgetConfig({ ...widgetConfig, feedbackEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ feedback_enabled: newValue });
                            showToast(newValue ? "Feedback Enabled" : "Feedback Disabled", "success");
                          } catch (error) {
                            console.error("Error updating feedback setting:", error);
                            showToast("Failed to update feedback setting", "error");
                          }
                        }}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.feedbackEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.feedbackEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">Streaming Responses</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Stream response tokens as they are generated instead of waiting for the full response.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.streamingEnabled;
                          setWidgetConfig({ ...widgetConfig, streamingEnabled: newValue });
                          try {
                            await api.patchWidgetConfig({ streaming_enabled: newValue });
                            showToast(newValue ? "Streaming Enabled" : "Streaming Disabled", "success");
                          } catch (error) {
                            console.error("Error updating streaming setting:", error);
                            showToast("Failed to update streaming setting", "error");
                          }
                        }}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.streamingEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.streamingEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-bold text-gray-900">Human Escalation</h4>
                        </div>
                        <p className="text-[11px] font-medium text-gray-400">Allow the agent to hand off to a human assistant when requested.</p>
                      </div>
                      <button
                        onClick={async () => {
                          const newValue = !widgetConfig.humanEscalation;
                          setWidgetConfig({ ...widgetConfig, humanEscalation: newValue });
                          try {
                            await api.patchWidgetConfig({ human_escalation: newValue });
                            showToast(newValue ? "Human Escalation Enabled" : "Human Escalation Disabled", "success");
                          } catch (error) {
                            console.error("Error updating human escalation setting:", error);
                            showToast("Failed to update human escalation setting", "error");
                          }
                        }}
                        className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.humanEscalation ? "border-[#83bbe1]" : "border-gray-200"}`}
                      >
                        <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.humanEscalation ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                      </button>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-2xl space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-gray-900">Neural Memory Summarization</h4>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400">Distill long conversations into summaries to optimize memory.</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newValue = !widgetConfig.summaryEnabled;
                            setWidgetConfig({ ...widgetConfig, summaryEnabled: newValue });
                            try {
                              await api.patchWidgetConfig({ summary_enabled: newValue });
                              showToast(newValue ? "Summarization Enabled" : "Summarization Disabled", "success");
                            } catch (error) {
                              console.error("Error updating summary setting:", error);
                              showToast("Failed to update summary setting", "error");
                            }
                          }}
                          className={`w-11 h-6 rounded-full border-2 transition-all relative bg-white ${widgetConfig.summaryEnabled ? "border-[#83bbe1]" : "border-gray-200"}`}
                        >
                          <div className={`absolute top-[2px] w-4 h-4 rounded-full transition-all ${widgetConfig.summaryEnabled ? "left-[22px] bg-[#83bbe1]" : "left-[2px] bg-gray-300"}`} />
                        </button>
                      </div>

                      {widgetConfig.summaryEnabled && (
                        <div className="pt-2 space-y-4">
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Summary Intelligence Model</label>
                            <Dropdown
                              options={SUMMARY_MODEL_OPTIONS}
                              value={widgetConfig.summaryModelId}
                              onChange={(val) => {
                                const newConfig = { ...widgetConfig, summaryModelId: val };
                                setWidgetConfig(newConfig);
                                const opt = SUMMARY_MODEL_OPTIONS.find(o => o.value === val);
                                saveConfig({ summary_model_id: val }, `Summarization model set to ${opt ? opt.label : val}`);
                              }}
                              buttonClassName="px-4 py-2 text-[10px] font-bold"
                              className="w-full"
                            />
                          </div>

                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-xs font-medium text-gray-600">Summarization Interval</span>
                              <span className="text-xs font-bold text-[#83bbe1]">Every {widgetConfig.summaryInterval} messages</span>
                            </div>
                            <input
                              type="range"
                              min="1"
                              max="20"
                              step="1"
                              value={widgetConfig.summaryInterval}
                              onChange={(e) => setWidgetConfig({ ...widgetConfig, summaryInterval: parseInt(e.target.value) })}
                              onMouseUp={async () => {
                                try {
                                  await api.patchWidgetConfig({ summary_interval: widgetConfig.summaryInterval });
                                  showToast(`Interval set to ${widgetConfig.summaryInterval} messages`, "success");
                                } catch (error) {
                                  console.error("Error updating summary interval:", error);
                                  showToast("Failed to update interval", "error");
                                }
                              }}
                              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#83bbe1]"
                            />
                            <div className="flex justify-between text-[10px] text-gray-400 font-medium">
                              <span>1 (Aggressive)</span>
                              <span>20 (Lazy)</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Neural Context Tokens Section */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-100/50 mt-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={neuralContextIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Neural Context Parameters</h3>
                  <p className="text-[11px] font-medium text-gray-400">Register custom parameters to pass from your site directly to the agent's memory.</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Common Parameters Dropdown */}
                  <Dropdown
                    options={COMMON_PARAMS}
                    value=""
                    onChange={(val) => {
                      const opt = COMMON_PARAMS.find(o => o.value === val);
                      const isRegistered = widgetConfig.customParameters.includes(val);
                      if (isRegistered) {
                        showToast(`"${val}" is already registered`, "warning");
                      } else {
                        const newParams = [...widgetConfig.customParameters, val];
                        setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                        saveConfig({ custom_parameters: newParams }, `${opt ? opt.label : val} added!`);
                      }
                    }}
                    buttonClassName="bg-[#f0f7ff] border border-[#83bbe1]/30 text-[#83bbe1] hover:bg-[#83bbe1]/10 px-5 py-2.5 tracking-tight font-bold"
                    className="flex-1"
                    renderSelected={() => (
                      <span className="flex items-center gap-2">
                        <Plus size={14} /> Default Parameters
                      </span>
                    )}
                    renderOption={(opt) => {
                      const isRegistered = widgetConfig.customParameters.includes(opt.value);
                      return (
                        <div className={`flex justify-between items-center w-full ${isRegistered ? "text-gray-300 cursor-not-allowed" : ""}`}>
                          <span>{opt.label}</span>
                          <span className="text-[10px] font-mono opacity-50">{opt.value}</span>
                        </div>
                      );
                    }}
                  />

                  <div className="flex items-center text-gray-300 px-2 font-bold text-[10px] uppercase">Or</div>

                  {/* Manual Input */}
                  <div className="flex gap-2 flex-[1.5]">
                    <input
                      id="new-param-input"
                      type="text"
                      placeholder="e.g. order_id"
                      className="flex-1 px-5 py-4 bg-gray-50 border-0 rounded-2xl text-sm text-gray-900 focus:ring-2 focus:ring-gray-900 placeholder:text-gray-400 transition-all"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                          if (!val) return;
                          if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                            showToast(`"${val}" is reserved.`, "error");
                            return;
                          }
                          if (!widgetConfig.customParameters.includes(val)) {
                            const newParams = [...widgetConfig.customParameters, val];
                            setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                            saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                            e.target.value = '';
                          } else {
                            showToast(`"${val}" exists.`, "warning");
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById('new-param-input');
                        const val = input.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
                        if (!val) return;
                        if (RESERVED_KEYS.some(k => k.toLowerCase() === val)) {
                          showToast(`"${val}" is reserved.`, "error");
                          return;
                        }
                        if (!widgetConfig.customParameters.includes(val)) {
                          const newParams = [...widgetConfig.customParameters, val];
                          setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                          saveConfig({ custom_parameters: newParams }, "Custom parameter added!");
                          input.value = '';
                        }
                      }}
                      className="px-6 py-4 bg-gray-900 text-white rounded-2xl hover:bg-gray-800 transition-all font-bold text-sm uppercase tracking-widest active:scale-95 shadow-md shadow-gray-200"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  {widgetConfig.customParameters.length === 0 ? (
                    <div className="w-full py-8 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-relaxed">
                        No custom parameters registered yet.<br />Parameters allow your app to talk to the AI without user input.
                      </p>
                    </div>
                  ) : (
                    widgetConfig.customParameters.map((param) => (
                      <div
                        key={param}
                        className="group flex items-center gap-3 px-4 py-2 bg-white border border-[#83bbe1]/20 rounded-xl shadow-sm hover:shadow-md hover:border-[#83bbe1]/50 transition-all cursor-default"
                      >
                        <span className="text-[11px] font-black text-[#111826] font-mono">
                          {param}
                        </span>
                        <button
                          onClick={() => {
                            const newParams = widgetConfig.customParameters.filter(p => p !== param);
                            setWidgetConfig({ ...widgetConfig, customParameters: newParams });
                            saveConfig({ custom_parameters: newParams }, "Neural parameter removed!");
                          }}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <ChevronDown size={14} className="rotate-45" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <p className="text-[9px] text-gray-400 font-medium uppercase tracking-tighter leading-relaxed">
                  * Note: Only lowercase alphanumeric and underscores are allowed. Once registered, these parameters will appear in your deployment script for your developers to populate.
                </p>

                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => saveConfig()}
                    className="flex items-center justify-center gap-3 px-10 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-gray-200 active:scale-95"
                  >
                    <Save size={14} />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>

          {/* New Sections: Live Campaigns */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50 w-full">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={liveEngagementIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Live Engagement Protocols</h3>
                  <p className="text-[11px] font-medium text-gray-400">Currently active campaigns on your widget</p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-bold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                {campaigns.filter(c => c.is_active).length} Active
              </div>
            </div>

            <div className="space-y-3">
              {campaigns.filter(c => c.is_active).length === 0 ? (
                <div className="py-6 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">No active protocols detected</p>
                </div>
              ) : (
                campaigns.filter(c => c.is_active).map(campaign => (
                  <div key={campaign.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100 hover:border-green-200 transition-all group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                        {campaign.data?.image ? (
                          campaign.data.image.startsWith('data:') || campaign.data.image.startsWith('http') || campaign.data.image.includes('/') || campaign.data.image.includes('.webp') || campaign.data.image.includes('static') ? (
                            <img
                              src={campaign.data.image}
                              alt={campaign.data?.name || 'Campaign'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-lg">{campaign.data.image}</span>
                          )
                        ) : (
                          '🏷️'
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">{campaign.data?.name || 'Untitled Campaign'}</h4>
                        <p className="text-[10px] text-gray-500 font-medium">Priority: P{campaign.priority} • Type: {campaign.data?.type || 'Standard'}</p>
                      </div>
                    </div>
                    <div className="px-3 py-1 bg-white border border-gray-100 rounded-lg text-[10px] font-black text-gray-400 uppercase tracking-tighter">
                      Live Now
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>


          <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl shadow-gray-100/50 w-full">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={deploymentScriptIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Deployment Script</h3>
                  <p className="text-[11px] font-medium text-gray-400">Inject into your site header</p>
                </div>
              </div>
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all font-bold text-xs"
              >
                <Copy size={14} />
                Copy Script
              </button>
            </div>

            <div className="relative group">
              <pre className="bg-gray-900 p-8 rounded-xl text-xs border-0 font-mono text-blue-400 leading-loose shadow-inner h-fit w-full whitespace-pre-wrap break-all">
                <code>{embedCode || "// Generating unique script..."}</code>
              </pre>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-xl shadow-gray-100/50 w-full">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                <img src={identityAvatarIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-xs uppercase tracking-widest">Identity Avatar</h3>
                <p className="text-[11px] font-medium text-gray-400">Visible brand trigger</p>
              </div>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() =>
                !uploading && document.getElementById("icon-input").click()
              }
              className={`group relative border-2 border-dashed border-gray-100 rounded-[24px] p-8 text-center bg-gray-50/50 hover:bg-white hover:border-blue-200 transition-all duration-300 ${uploading ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              {uploading ? (
                <div className="flex flex-col items-center py-6">
                  <div className="w-10 h-10 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  {iconPreview ? (
                    <div className="mb-6">
                      <img
                        src={iconPreview}
                        alt="Widget Icon"
                        className="w-24 h-24 object-contain rounded-2xl shadow-xl ring-4 ring-white ring-offset-4 ring-gray-50/50"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center text-gray-200 mb-6 shadow-sm border border-gray-50 group-hover:scale-105 transition-transform duration-300">
                      <MessageSquare size={40} />
                    </div>
                  )}
                  <h4 className="text-sm font-bold text-gray-900 mb-2">Change Avatar</h4>
                  <p className="text-[10px] text-gray-400 font-medium leading-relaxed uppercase tracking-tighter">
                    Drag and drop here or<br />click to select file
                  </p>
                </div>
              )}
            </div>

            <input
              id="icon-input"
              type="file"
              accept="image/*"
              onChange={(e) => handleIconUpload(e.target.files[0])}
              className="hidden"
            />
            <p className="text-[10px] text-center text-gray-400 mt-6 font-bold uppercase tracking-widest">
              SVG, PNG OR JPG • Max 2MB
            </p>
          </div>
          </div>
          {/* END: LEFT Settings Panel */}

          {/* RIGHT: Sticky Live Preview Panel */}
          <div style={{
            width: '340px',
            flexShrink: 0,
            position: 'sticky',
            top: '24px',
            alignSelf: 'flex-start'
          }}>
            {/* Widget Phone Frame */}
            <div style={{
              background: '#ffffff',
              borderRadius: widgetConfig.borderRadius || '24px',
              overflow: 'visible',
              boxShadow: '0 20px 50px -10px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.06)',
              display: 'flex',
              flexDirection: 'column',
              height: '560px',
              position: 'relative'
            }}>

                {/* Tech Canvas Background (Toned to Header Color, clipped to frame border radius) */}
                {(() => {
                  const previewPrimaryColor = extractBaseHex(widgetConfig.headerColor, '#4c3850');
                  return (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: widgetConfig.borderRadius || '24px',
                      overflow: 'hidden',
                      pointerEvents: 'none',
                      zIndex: 0,
                      background: `linear-gradient(160deg, color-mix(in srgb, ${previewPrimaryColor} 12%, #ffffff) 0%, color-mix(in srgb, ${previewPrimaryColor} 18%, #f8fafc) 50%, color-mix(in srgb, ${previewPrimaryColor} 28%, #eef2f6) 100%)`
                    }}>
                      {/* Subtle Grid Pattern tinted to header color */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `linear-gradient(to right, color-mix(in srgb, ${previewPrimaryColor} 26%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in srgb, ${previewPrimaryColor} 26%, transparent) 1px, transparent 1px)`,
                        backgroundSize: '38px 38px',
                        opacity: 0.4,
                        maskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 40%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 70% 65% at 50% 50%, #000 40%, transparent 100%)'
                      }} />
                      {/* Glowing Ambient Orbs tinted to header color */}
                      <div style={{
                        position: 'absolute',
                        top: '-50px',
                        left: '-50px',
                        width: '260px',
                        height: '260px',
                        borderRadius: '50%',
                        background: previewPrimaryColor,
                        opacity: 0.18,
                        filter: 'blur(55px)',
                        WebkitFilter: 'blur(55px)'
                      }} />
                      <div style={{
                        position: 'absolute',
                        bottom: '-60px',
                        right: '-60px',
                        width: '280px',
                        height: '280px',
                        borderRadius: '50%',
                        background: previewPrimaryColor,
                        opacity: 0.20,
                        filter: 'blur(60px)',
                        WebkitFilter: 'blur(60px)'
                      }} />
                      <div style={{
                        position: 'absolute',
                        top: '35%',
                        right: '15%',
                        width: '190px',
                        height: '190px',
                        borderRadius: '50%',
                        background: previewPrimaryColor,
                        opacity: 0.14,
                        filter: 'blur(40px)',
                        WebkitFilter: 'blur(40px)'
                      }} />
                    </div>
                  );
                })()}

                {/* Widget Header Area with stable hover boundary */}
                <div
                  onMouseEnter={() => setHoveredPreviewElement('header')}
                  onMouseLeave={() => setHoveredPreviewElement(null)}
                  style={{
                    position: 'relative',
                    zIndex: 20,
                    flexShrink: 0
                  }}
                >
                  <div
                    onClick={() => scrollToColorCustomization('headerColor')}
                    style={{
                      background: widgetConfig.headerColor || '#4c3850',
                      padding: '16px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      borderRadius: `${widgetConfig.borderRadius || '24px'} ${widgetConfig.borderRadius || '24px'} 0 0`,
                      transition: 'transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.28s cubic-bezier(0.2, 0.8, 0.2, 1)',
                      transform: hoveredPreviewElement === 'header' ? 'translateY(-14px)' : 'translateY(0)',
                      boxShadow: hoveredPreviewElement === 'header'
                        ? '0 22px 42px -4px rgba(0,0,0,0.38), 0 8px 18px rgba(0,0,0,0.18)'
                        : '0 0 0 0 rgba(0,0,0,0)',
                      willChange: 'transform, box-shadow',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    {/* Bot Avatar / Brand Logo */}
                    <div style={{
                      maxHeight: '36px',
                      maxWidth: '42px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {(brandLogoPreview || iconPreview) ? (
                        <img
                          src={brandLogoPreview || iconPreview}
                          alt="brand logo"
                          style={{ maxHeight: '100%', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
                          onError={(e) => {
                            if (iconPreview && e.currentTarget.src !== iconPreview) {
                              e.currentTarget.src = iconPreview;
                            }
                          }}
                        />
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        onMouseEnter={() => setHoveredPreviewElement('headerTitle')}
                        onMouseLeave={() => setHoveredPreviewElement(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          scrollToColorCustomization('headerTitleColor');
                        }}
                        style={{
                          color: widgetConfig.headerTitleColor || '#ffffff',
                          fontWeight: 700,
                          fontSize: '14px',
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          display: 'inline-block',
                          maxWidth: '100%',
                          transition: 'all 0.24s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          transform: hoveredPreviewElement === 'headerTitle' ? 'translateY(-2px) scale(1.04)' : 'none',
                          textShadow: hoveredPreviewElement === 'headerTitle' ? '0 3px 10px rgba(0,0,0,0.35)' : 'none'
                        }}
                      >
                        {widgetConfig.title || 'Chat Assistant'}
                      </div>
                    </div>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      background: 'rgba(255,255,255,0.18)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Messages Area (transparent so canvas background shows through) */}
                <div style={{
                  flex: 1,
                  padding: '18px 16px 82px 16px',
                  background: 'transparent',
                  overflowY: 'auto',
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px'
                }}>
                  {/* Welcome Message */}
                  {widgetConfig.welcomeMessage && (
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <div
                        onMouseEnter={() => setHoveredPreviewElement('bot_welcome')}
                        onMouseLeave={() => setHoveredPreviewElement(null)}
                        onClick={() => scrollToColorCustomization('botBubbleColor')}
                        style={{
                          background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                          backdropFilter: 'blur(16px)',
                          WebkitBackdropFilter: 'blur(16px)',
                          borderRadius: '4px 20px 20px 20px',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                          padding: '12px 16px',
                          fontSize: '13px',
                          color: '#0f172a',
                          maxWidth: '82%',
                          fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                          lineHeight: 1.5,
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: hoveredPreviewElement === 'bot_welcome' ? 15 : 1,
                          transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          transform: hoveredPreviewElement === 'bot_welcome' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                          boxShadow: hoveredPreviewElement === 'bot_welcome'
                            ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                            : '0 4px 15px rgba(0, 0, 0, 0.05)',
                        }}
                      >
                        {widgetConfig.welcomeMessage}
                      </div>
                    </div>
                  )}

                  {/* Sample bot message */}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('bot_sample')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('botBubbleColor')}
                      style={{
                        background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        borderRadius: '4px 20px 20px 20px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#0f172a',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'bot_sample' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'bot_sample' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'bot_sample'
                          ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                          : '0 4px 15px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      👋 Hi! How can I help you today?
                    </div>
                  </div>

                  {/* Sample user message */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexDirection: 'row-reverse' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('user')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('userBubbleColor')}
                      style={{
                        background: widgetConfig.userBubbleColor || '#4c3850',
                        borderRadius: '20px 20px 4px 20px',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#ffffff',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'user' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'user' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'user'
                          ? '0 18px 36px -4px rgba(0,0,0,0.28), 0 6px 14px rgba(0,0,0,0.12)'
                          : '0 4px 15px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      I have a question about my order.
                    </div>
                  </div>

                  {/* Bot response */}
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div
                      onMouseEnter={() => setHoveredPreviewElement('bot_response')}
                      onMouseLeave={() => setHoveredPreviewElement(null)}
                      onClick={() => scrollToColorCustomization('botBubbleColor')}
                      style={{
                        background: widgetConfig.botBubbleColor || 'rgba(255, 255, 255, 0.88)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        borderRadius: '4px 20px 20px 20px',
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        padding: '12px 16px',
                        fontSize: '13px',
                        color: '#0f172a',
                        maxWidth: '82%',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif',
                        lineHeight: 1.5,
                        cursor: 'pointer',
                        position: 'relative',
                        zIndex: hoveredPreviewElement === 'bot_response' ? 15 : 1,
                        transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: hoveredPreviewElement === 'bot_response' ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                        boxShadow: hoveredPreviewElement === 'bot_response'
                          ? '0 18px 36px -4px rgba(0,0,0,0.22), 0 6px 14px rgba(0,0,0,0.08)'
                          : '0 4px 15px rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      Sure! Please share your order ID and I'll look it up right away.
                    </div>
                  </div>
                </div>

                {/* Floating Input Island Dock (matching original widget) */}
                <div style={{
                  position: 'absolute',
                  bottom: '14px',
                  left: '14px',
                  right: '14px',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  pointerEvents: 'none'
                }}>
                  {/* Input Pill Wrapper */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    background: '#ffffff',
                    borderRadius: '26px',
                    boxShadow: '0 8px 24px -4px rgba(15, 23, 42, 0.10), 0 2px 6px 0 rgba(15, 23, 42, 0.04)',
                    padding: '0 12px 0 16px',
                    pointerEvents: 'auto'
                  }}>
                    <input
                      readOnly
                      placeholder="Type a message..."
                      style={{
                        flex: 1,
                        padding: '12px 6px 12px 0',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: '13px',
                        color: '#475569',
                        fontFamily: widgetConfig.fontFamily || 'Inter, sans-serif'
                      }}
                    />
                    <button
                      type="button"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#94a3b8'
                      }}
                    >
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </button>
                  </div>

                  {/* Send Button (3D Hover & Click) */}
                  <div
                    onMouseEnter={() => setHoveredPreviewElement('send')}
                    onMouseLeave={() => setHoveredPreviewElement(null)}
                    onClick={() => scrollToColorCustomization('sendButtonColor')}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      background: widgetConfig.sendButtonColor || '#4c3850',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      cursor: 'pointer',
                      position: 'relative',
                      zIndex: hoveredPreviewElement === 'send' ? 20 : 1,
                      pointerEvents: 'auto',
                      transition: 'all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transform: hoveredPreviewElement === 'send' ? 'translateY(-8px) scale(1.15)' : 'translateY(0) scale(1)',
                      boxShadow: hoveredPreviewElement === 'send'
                        ? '0 20px 36px -2px rgba(0,0,0,0.42), 0 8px 16px rgba(0,0,0,0.2)'
                        : '0 8px 22px -2px rgba(15, 23, 42, 0.20), inset 0 1px 1.5px rgba(255, 255, 255, 0.4)',
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ transform: 'rotate(45deg) translate(-1px, 1px)' }}>
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  </div>
                </div>
              </div>

           

          </div>
          {/* END: Right Preview Panel */}

        </div>
        {/* END: Two-column layout */}

      </div>
    </div>
  );
};

export default WidgetIntegration;
